import { useEffect, useMemo, useState } from "react";
import { ChevronDown } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/utils/cn";
import type { ReasonPanelProps } from "./reasonPanel.types";
import {
  buildMinDateDisabled,
  formatDateString,
  intervalOverlapsRanges,
  isDateInAnyRanges,
  parseDateString,
} from "./datePickerRules";

export default function LeaveReasonPanel({
  joiningDate,
  startDate,
  onStartDateChange,
  isStartDateError,
  resignationDate,
  onResignationDateChange,
  isEndDateError,
  memo,
  onMemoChange,
  disabled,
  occupiedRanges = [],
}: ReasonPanelProps) {
  const [isStartPickerOpen, setIsStartPickerOpen] = useState(false);
  const [isEndPickerOpen, setIsEndPickerOpen] = useState(false);

  const selectedStartDate = useMemo(
    () => parseDateString(startDate),
    [startDate]
  );
  const selectedEndDate = useMemo(
    () => parseDateString(resignationDate),
    [resignationDate]
  );
  const minStartDate = useMemo(() => parseDateString(joiningDate), [joiningDate]);
  const minEndDate = useMemo(
    () => parseDateString(startDate || joiningDate),
    [startDate, joiningDate]
  );

  useEffect(() => {
    if (!startDate || !resignationDate) return;
    const startDateObj = parseDateString(startDate);
    const endDateObj = parseDateString(resignationDate);
    if (!startDateObj || !endDateObj) return;
    if (endDateObj < startDateObj) {
      onResignationDateChange("");
    }
  }, [startDate, resignationDate, onResignationDateChange]);

  return (
    <div className="flex flex-col gap-3 pt-2">
      <div className="flex flex-col gap-2">
        <div className="grid grid-cols-[1fr_auto_1fr] gap-2">
          <label className="text-[14px] font-semibold text-[#939393]">
            휴직 시작일 <span className="text-[#C0392B]">*</span>
          </label>
          <span />
          <label className="text-[14px] font-semibold text-[#939393]">
            휴직 종료일 <span className="text-[#C0392B]">*</span>
          </label>
        </div>
        <div className="flex items-center gap-2 min-w-0">
          <div className="flex-1 min-w-0">
            <Popover open={isStartPickerOpen} onOpenChange={setIsStartPickerOpen}>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  disabled={disabled ?? false}
                  className={cn(
                    "w-full h-[35px] justify-between px-[12px] py-2 text-[15px] font-normal hover:bg-white min-w-0",
                    isStartDateError ? "border-destructive" : "border-[#D6D6DE]"
                  )}
                >
                  <span className="truncate">{startDate || "YYYY-MM-DD"}</span>
                  <ChevronDown className="h-4 w-4 text-[#939393] shrink-0" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={selectedStartDate}
                  onSelect={(date) => {
                    onStartDateChange(date ? formatDateString(date) : "");
                    setIsStartPickerOpen(false);
                  }}
                  disabled={(date) => {
                    if (buildMinDateDisabled(minStartDate)(date)) return true;
                    if (isDateInAnyRanges(date, occupiedRanges)) return true;
                    if (resignationDate) {
                      const startStr = formatDateString(date);
                      if (
                        intervalOverlapsRanges(
                          startStr,
                          resignationDate,
                          occupiedRanges
                        )
                      )
                        return true;
                    }
                    return false;
                  }}
                  captionLayout="label"
                />
              </PopoverContent>
            </Popover>
          </div>
          <span className="text-[#B2B2B8]">~</span>
          <div className="flex-1 min-w-0">
            <Popover open={isEndPickerOpen} onOpenChange={setIsEndPickerOpen}>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  disabled={(disabled ?? false) || !startDate}
                  className={cn(
                    "w-full h-[35px] justify-between px-[12px] py-2 text-[15px] font-normal hover:bg-white min-w-0",
                    isEndDateError ? "border-destructive" : "border-[#D6D6DE]"
                  )}
                >
                  <span className="truncate">{resignationDate || "YYYY-MM-DD"}</span>
                  <ChevronDown className="h-4 w-4 text-[#939393] shrink-0" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={selectedEndDate}
                  onSelect={(date) => {
                    onResignationDateChange(date ? formatDateString(date) : "");
                    setIsEndPickerOpen(false);
                  }}
                  disabled={(date) => {
                    if (buildMinDateDisabled(minEndDate)(date)) return true;
                    if (isDateInAnyRanges(date, occupiedRanges)) return true;
                    if (startDate) {
                      const endStr = formatDateString(date);
                      if (
                        intervalOverlapsRanges(startDate, endStr, occupiedRanges)
                      )
                        return true;
                    }
                    return false;
                  }}
                  captionLayout="label"
                />
              </PopoverContent>
            </Popover>
          </div>
        </div>
        {(isStartDateError || isEndDateError) && (
          <div className="grid grid-cols-[1fr_auto_1fr] gap-2">
            <p className="text-destructive text-xs">
              {isStartDateError ? "* 필수 값입니다" : ""}
            </p>
            <span />
            <p className="text-destructive text-xs">
              {isEndDateError ? "* 필수 값입니다" : ""}
            </p>
          </div>
        )}
      </div>

      <Textarea
        className="resize-none min-h-[64px] text-sm"
        placeholder="휴직 사유"
        value={memo ?? ""}
        onChange={(e) => onMemoChange(e.target.value)}
        disabled={(disabled ?? false) || !startDate || !resignationDate}
      />
    </div>
  );
}
